// field-value.ts — pre-connect-sicherer Wert für Feld-WCs (EIN uniformer Kern).
//
// Problem: Feld-WCs definierten ihren Wert-Getter bislang in render() — und
// render() läuft im connectedCallback. Folge: an einem DETACHTEN Element
// (createElement, noch nicht im DOM) ist `el.value`/`el.checked === undefined`.
// Jeder Consumer, der den Wert synchron zur Build-Zeit liest (z.B. initiale
// Formular-Validierung, bevor die Card ins Shadow gehängt wird), stolpert darüber
// (PFS-645-Fund: mednet-practice `refresh()`).
//
// Lösung: den Accessor im Konstruktor installieren (defineComponent .created-Seam)
// — mit einem Puffer, der VOR dem Binden des Controls den gewünschten Wert hält
// (explizit gesetzt ODER Initial-Attribut) und nach dem Binden auf das echte
// Control durchgreift. Damit ist der Wert ab Konstruktion les- UND schreibbar.
//
// EIN Wert-Kern für ALLE Feld-Primitive (PFS-650). Zwei Achsen:
//   - Property/Typ:  `.value` (string) | `.checked` (boolean).
//   - Wahrheitsquelle:
//       (a) natives Control (input/select/textarea — dessen .value/.checked trägt
//           die Wahrheit): text/select/textarea/search + checkbox.
//       (b) Puffer (radio-group/scope-toggle): der Wert ist EINE Auswahl aus
//           gerenderten Optionen — es gibt kein einzelnes Control → der Puffer ist
//           die Wahrheit, eine paint()-Naht spiegelt ihn ins DOM. (Nebeneffekt:
//           die Reihenfolge options-vs-value ist egal — der Getter liest nie DOM.)
//
// Nähte (benannte Seams statt Config-Flag):
//   - installFieldValue / installFieldChecked  → Accessor + Puffer im .created-Seam.
//   - bindFieldControl(host, control)           → natives Control ist Wahrheit (a).
//   - bindSelectionValue(host, paint)           → Puffer ist Wahrheit (b), gibt commit().
//   - reapplyFieldValue(host)                   → Re-Apply nach Options-Rebuild (<select>).
//   - readFieldValue(host)                      → aktuellen Wert lesen (für paint()).
import { takeOwnProperty } from "../utils/upgrade-property.js";

type FieldProp = "value" | "checked";
type FieldValue = string | boolean;

/** Ein natives Control, dessen `.value`/`.checked` die Wahrheit trägt. */
type ValueControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

interface FieldValueState {
  prop: FieldProp; // welche Property der Accessor trägt (value|checked)
  explicit: boolean; // wurde die Property je programmatisch gesetzt?
  buffer: FieldValue; // gepufferter Wert (pre-connect / Re-Apply / Auswahl-Wahrheit)
  control: ValueControl | null; // gebundenes natives Control (nach render, Fall a)
  reflect: ((v: FieldValue) => void) | null; // Auswahl-Naht: spiegelt Puffer ins DOM (Fall b)
  onApplied: (() => void) | null; // Naht: läuft nach jedem Wert-Apply (z.B. readonly-Box neu malen)
}

const STATES = new WeakMap<HTMLElement, FieldValueState>();

const coerce = (prop: FieldProp, v: unknown): FieldValue =>
  prop === "checked" ? Boolean(v) : v == null ? "" : String(v);

// Wert vom nativen Control lesen/schreiben — prop-abhängig (checked nur an <input>).
const readControl = (control: ValueControl, prop: FieldProp): FieldValue =>
  prop === "checked" ? (control as HTMLInputElement).checked : control.value;

const writeControl = (control: ValueControl, prop: FieldProp, v: FieldValue): void => {
  if (prop === "checked") (control as HTMLInputElement).checked = Boolean(v);
  else control.value = String(v);
};

// Gewünschter Wert = explizit gesetzter Puffer, sonst das Initial-Attribut. LAZY
// (bei get) gelesen — im Konstruktor sind Attribute per Spec noch nicht verlässlich
// gesetzt. checked: Präsenz von data-checked = true; value: data-value ?? "".
function desired(host: HTMLElement, s: FieldValueState): FieldValue {
  if (s.explicit) return s.buffer;
  return s.prop === "checked" ? host.dataset.checked != null : (host.dataset.value ?? "");
}

// Gemeinsamer Kern: installiert den pre-connect-sicheren Accessor (im .created-Seam)
// für value (string) oder checked (boolean).
function installFieldState(host: HTMLElement, prop: FieldProp): void {
  const pre = takeOwnProperty(host, prop); // vor Upgrade gesetzt?
  const s: FieldValueState = {
    prop,
    explicit: pre !== undefined,
    buffer: pre !== undefined ? coerce(prop, pre) : coerce(prop, prop === "checked" ? false : ""),
    control: null,
    reflect: null,
    onApplied: null,
  };
  STATES.set(host, s);
  Object.defineProperty(host, prop, {
    configurable: true,
    get: () => (s.control ? readControl(s.control, prop) : desired(host, s)),
    set: (v: unknown) => {
      s.explicit = true;
      s.buffer = coerce(prop, v);
      if (s.control) {
        writeControl(s.control, prop, s.buffer);
        s.onApplied?.();
      } else if (s.reflect) {
        s.reflect(s.buffer); // Auswahl-Control: Puffer ins DOM spiegeln
      }
    },
  });
}

/** Installiert den pre-connect-sicheren `value`-Accessor (string). Im .created-Seam. */
export function installFieldValue(host: HTMLElement): void {
  installFieldState(host, "value");
}

/** Installiert den pre-connect-sicheren `checked`-Accessor (boolean). Im .created-Seam. */
export function installFieldChecked(host: HTMLElement): void {
  installFieldState(host, "checked");
}

/**
 * Bindet ein natives Control (input/select/textarea) an den Accessor — Fall (a),
 * das Control trägt die Wahrheit. In render() aufrufen, NACHDEM das Control im
 * Shadow hängt (bei <select> VOR dem Options-Aufbau — die Auswahl setzt
 * reapplyFieldValue nach). Wendet den gewünschten Wert an und hält den Puffer bei
 * User-Interaktion synchron (für Re-Apply + überlebt Re-Render). Funktioniert für
 * value UND checked (prop steckt im State).
 */
export function bindFieldControl(
  host: HTMLElement,
  control: ValueControl,
  onValueApplied?: () => void,
): void {
  const s = STATES.get(host);
  if (!s) return; // created lief nicht → defensiv (No-op)
  s.control = control;
  s.onApplied = onValueApplied ?? null;
  writeControl(control, s.prop, desired(host, s));
  s.onApplied?.();
  const sync = (): void => {
    s.explicit = true;
    s.buffer = readControl(control, s.prop);
    s.onApplied?.();
  };
  control.addEventListener("input", sync);
  control.addEventListener("change", sync);
}

/**
 * Bindet ein Auswahl-Control (radio-group/scope-toggle) an den Accessor — Fall (b),
 * der PUFFER trägt die Wahrheit (keine einzelne Control-Property). `paint(value)`
 * spiegelt den gewünschten Wert ins DOM (Radios/Segmente neu markieren). Der
 * zurückgegebene `commit(value)` wird aus dem User-Interaktions-Handler aufgerufen
 * und schreibt den Puffer + spiegelt. Programmatisches `.value =` läuft über den
 * Accessor-Setter → `s.reflect` → dasselbe paint(). Reihenfolge options/value egal.
 */
export function bindSelectionValue(
  host: HTMLElement,
  paint: (value: string) => void,
): (value: string) => void {
  const s = STATES.get(host);
  if (!s) return () => {}; // created lief nicht → defensiv
  s.reflect = (v) => paint(String(v));
  paint(String(desired(host, s))); // Initial-Spiegelung (Puffer / data-value)
  return (value: string): void => {
    s.explicit = true;
    s.buffer = value;
    paint(value);
  };
}

/**
 * Wendet den gewünschten Wert erneut auf das gebundene native Control an — nötig,
 * wenn das Control seine Optionen neu aufbaut (<select>), was die Auswahl sonst
 * verwirft. No-op ohne gebundenes Control (Auswahl-Controls spiegeln über paint()).
 */
export function reapplyFieldValue(host: HTMLElement): void {
  const s = STATES.get(host);
  if (s?.control) {
    writeControl(s.control, s.prop, desired(host, s));
    s.onApplied?.();
  }
}

/** Aktuellen Wert als String lesen (für paint() in Auswahl-Controls). */
export function readFieldValue(host: HTMLElement): string {
  const s = STATES.get(host);
  if (!s) return "";
  return String(s.control ? readControl(s.control, s.prop) : desired(host, s));
}
